import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './App.css';
import { useSpring, a } from '@react-spring/web';
import GooeyLink from './GooeyLink';
import { useTheme } from './ThemeContext';

// Import all images from the DIA folder
import dia1 from './images/DIA/1.jpg';
import dia2 from './images/DIA/2.png';
import dia3 from './images/DIA/3.png';
import dia4 from './images/DIA/4.jpg';
import dia5 from './images/DIA/5.jpg';
import dia6 from './images/DIA/6.jpg';
import dia7 from './images/DIA/7.jpg';
import dia8 from './images/DIA/8.png';
import dia9 from './images/DIA/9.jpeg';
import dia10 from './images/DIA/10.png';

function DiaTeaCeremony() {
  // Create an array of all images
  const diaImages = [dia1, dia2, dia3, dia8, dia9, dia10];
  // Replace local state with context
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [{ color }] = useSpring({ 
    color: isDarkMode ? '#FFFFFF' : '#000000' 
  }, [isDarkMode]);
  
  // Add refs for the Vimeo player
  const vimeoPlayerRef = useRef(null);
  const vimeoIframeRef = useRef(null);
  
  // Add this near the top of your component
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Initialize Vimeo player with useEffect
  useEffect(() => {
    // Load the Vimeo Player API script
    const script = document.createElement("script");
    script.src = "https://player.vimeo.com/api/player.js";
    script.async = true;
    
    script.onload = () => {
      // Once the API is loaded, initialize the player
      if (vimeoIframeRef.current) {
        const player = new window.Vimeo.Player(vimeoIframeRef.current);
        vimeoPlayerRef.current = player;
        
        // Set initial time to 12 seconds
        player.setCurrentTime(12);
        
        // Add listener for timeupdate event
        player.on('timeupdate', function(data) {
          // If video passes 45 seconds, go back to 12 seconds
          if (data.seconds >= 45) {
            player.setCurrentTime(12).then(() => {
              // Ensure playback continues after seeking
              player.play();
            }).catch(error => {
              console.error('Error setting time:', error);
            });
          }
        });
      }
    };
    
    document.body.appendChild(script);
    
    return () => {
      // Clean up
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  
  // Add resize effect
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return (
    <>
      {/* Full-width background */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: isDarkMode ? '#121212' : '#EFEFEF',
        zIndex: -1
      }} />
      
      <div className="tea-ceremony-page" style={{ 
        margin: '0 36px',
        position: 'relative',
        minHeight: '100vh'
      }}>
        {/* Navigation Bar - same as main page */}
        <a.div className="App-navigation" style={{ 
          color,
          position: 'relative',
          width: '100%',
          padding: '24px 0',
          marginTop: '0',
          transition: 'background 0.3s ease, color 0.3s ease'
        }}>
          <Link to="/" style={{ textDecoration: 'none', 
                WebkitTapHighlightColor: 'transparent' // Prevents tap highlight on mobile

          }}>
            <a.span className="name" style={{ color }}>Lou Kishfy</a.span>
          </Link>
          <div className="citySpan" style={{ 
            position: 'absolute', 
            left: '50%', 
            transform: 'translateX(-50%)',
            top: '26px',
            width: 'auto',
            textAlign: 'center'
          }}>
            <a.span style={{ color }}>New York, New York</a.span>
          </div>
          <div className="nav-links">
            <a.span className="link-spacing" style={{ 
              color,
              transform: 'translate(16px, -.5px)', 
              display: 'inline-block'
            }}>
              <GooeyLink 
                href="mailto:louis.kishfy@gmail.com?subject=Hey Lou, reaching out to..." 
                target="_blank"
                isDarkMode={isDarkMode}
                textColor={color}
              >
                Contact Me
              </GooeyLink>
            </a.span>
          </div>
        </a.div>
        
        {/* Grey divider that spans full width */}
        <div style={{ 
          width: '100%',
          height: '1px', 
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', 
          margin: '48px 0px 16px 0px',
          position: 'relative',
        }}></div>
        
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{ fontSize: '48px', fontWeight: '600', color: isDarkMode ? '#FFFFFF' : '#000000' }}>Japanese Tea Ceremony</h1>
          <p style={{ fontSize: '18px', maxWidth: '800px', color: isDarkMode ? '#FFFFFF' : '#000000' }}>
          Software development in Electron, including fiducial tracking and shell scripting for museum automation. The application was programmed to dynamically change the season of the video based on the current date.
          </p>
        </div>
        
        {/* Info and Team Section */}
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row', 
          marginBottom: '60px',
          gap: isMobile ? '30px' : '60px'
        }}>
          {/* Info Section */}
          <div style={{ flex: '1' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600',
              marginBottom: '12px',
              color: isDarkMode ? '#FFFFFF' : '#000000'
            }}>
              Info
            </h3>
            <p style={{ 
              fontSize: '16px',
              lineHeight: '1.5',
              color: isDarkMode ? '#FFFFFF' : '#000000'
            }}>
The Japanese Tea Ceremony experience provides an opportunity for visitors to make a personal connection to the Japanese Way of Tea by reinterpreting the stillness and movement of a typical tea ceremony. Sitting at a giant digital table, visitors move through a digital storytelling experience using a 3D-printed tea bowl inspired by Japanese aesthetics found in the DIA collection. The animated story highlights the intricate process of making matcha tea, and thoughtfully considers the different roles and dynamics at play between the host and guests during a ceremony.
<br></br>
<br></br>
Traditional masterpieces are displayed alongside the interactive installation to explore the complementary qualities of stillness and movement inherent in many Japanese artworks. Visitors are invited to imagine the objects in their original contexts and spaces, such as tea rooms, Buddhist temples and domestic rooms. 
<br></br>
<br></br>
We started the journey by diving head first into research. Our team participated in a culturally specific, proper Japanese Tea Ceremony in New York City — in the same building that used to be Mark Rothko's studio.
            </p>
          </div>
          
          {/* Team Section */}
          <div style={{ flex: '1' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600',
              marginBottom: '12px',
              color: isDarkMode ? '#FFFFFF' : '#000000'
            }}>
              Team
            </h3>
            <p style={{ 
              fontSize: '16px',
              lineHeight: '1.5',
              color: isDarkMode ? '#FFFFFF' : '#555' // Dark grey for team text
            }}>
              Software Development: Lou Kishfy<br />
              Design: Lou Kishfy, James Smalls, Haiden Goggin<br />
             Project Manager: Kara Sanchez<br />
            </p>
          </div>
        </div>
        
        {/* Add the video container above the images */}
        <div style={{ marginBottom: '24px' }}>
          <div className="video-container" style={{
            position: 'relative',
            paddingBottom: '56.25%', // 16:9 aspect ratio
            height: 0,
            overflow: 'hidden',
            maxWidth: '100%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }}>
            <iframe
              ref={vimeoIframeRef}
              src="https://player.vimeo.com/video/336296257?autoplay=1&background=1&muted=1"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Japanese Tea Ceremony Video"
            ></iframe>
          </div>
        </div>
        
        {/* Full-width images */}
        <div className="dia-images">
          {diaImages.map((image, index) => (
            <div key={index} className="dia-image-container" style={{ marginBottom: '20px' }}>
              <img 
                src={image} 
                alt={`Japanese Tea Ceremony - Image ${index + 1}`} 
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  display: 'block'
                }} 
              />
            </div>
          ))}
        </div>
        
        {/* Full-width divider before footer */}
        <div style={{ 
          width: 'calc(100% + 72px)', // 100% plus the left and right margins (36px each)
          height: '1px', 
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', 
          margin: '60px -36px 0 -36px', // Negative margins to counter the page margins
          position: 'relative', // Ensure proper positioning
        }}></div>
        
        {/* Footer - exact match from App.js */}
        <a.div id="contact" className="footer" style={{ 
          marginTop: '40px',
          paddingTop: '0px',
          paddingBottom: '40px',
          paddingLeft: '36px',
          color
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: '20px',
            marginBottom: '20px',
            marginLeft: '0px'
          }}>
            <h3 style={{ 
              fontSize: '24px', 
              fontWeight: '600', 
              marginBottom: '16px',
              marginLeft: '0px'
            }}>Find Me</h3>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '20px',
              marginBottom: '10px'
            }}>
              <GooeyLink 
                href="mailto:louis.kishfy@gmail.com" 
                isDarkMode={isDarkMode}
              >
                Email
              </GooeyLink>
              <GooeyLink 
                href="https://www.linkedin.com/in/louis-kishfy/" 
                target="_blank" 
                isDarkMode={isDarkMode}
              >
                LinkedIn
              </GooeyLink>
              <GooeyLink 
                href="https://github.com/LouisKishfy" 
                target="_blank" 
                isDarkMode={isDarkMode}
              >
                GitHub
              </GooeyLink>
              <GooeyLink 
                href="https://www.figma.com/@LouKishfy" 
                target="_blank" 
                isDarkMode={isDarkMode}
              >
                Figma
              </GooeyLink>
            </div>
            <div style={{ textAlign: 'left', display: 'inline-block', marginLeft: '0px' }}>
              <GooeyLink 
                href="https://x.com/louiskishfy" 
                target="_blank" 
                isDarkMode={isDarkMode}
                textColor={color}
              >
                X
              </GooeyLink>
            </div>
          </div>
          <div style={{ 
            marginTop: '30px', 
            textAlign: 'left',
            fontSize: '14px',
            opacity: 0.7,
            width: '100%'
          }}>
            <a.p style={{ color }}>© {new Date().getFullYear()} Lou Kishfy. All rights reserved.</a.p>
          </div>
        </a.div>
      </div>
    </>
  );
}

export default DiaTeaCeremony; 